import { useTranslation } from 'react-i18next';
import { Search, MapPin } from 'lucide-react';

const ClientSection = ({ formData, setFormData, dealerSearch }) => {
  const { t } = useTranslation();
  const {
    dealerSuggestions, osmSuggestions,
    showDealerDropdown, setShowDealerDropdown,
    selectedDealerLocations, showLocationDropdown,
    isSearching, dealerInputRef,
    handleDealerSearchChange, selectDealer, selectOsmResult, selectLocation,
  } = dealerSearch;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="relative">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('appointment.clientName')} *
          <span className="text-xs text-gray-400 ml-2">(digita per cercare)</span>
        </label>
        <div className="relative">
          <input
            ref={dealerInputRef}
            type="text"
            value={formData.client_name}
            onChange={handleDealerSearchChange}
            onFocus={() => formData.client_name?.length >= 2 && setShowDealerDropdown(true)}
            onBlur={() => setTimeout(() => setShowDealerDropdown(false), 300)}
            className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-lojack-red"
            required
            data-testid="appointment-client-name"
            placeholder="Es: Concessionaria Rossi..."
            autoComplete="off"
          />
          <Search size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
        </div>

        {/* Dealer Suggestions Dropdown */}
        {showDealerDropdown && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-y-auto">
            {isSearching && (
              <div className="px-4 py-3 text-center text-gray-500 text-sm">Ricerca in corso...</div>
            )}
            {!isSearching && dealerSuggestions.length > 0 && (
              <>
                <div className="px-4 py-2 bg-green-50 border-b sticky top-0">
                  <span className="text-xs font-semibold text-green-700">DAL TUO DATABASE ({dealerSuggestions.length})</span>
                </div>
                {dealerSuggestions.map((dealer, idx) => (
                  <button key={`db-${idx}`} type="button" onClick={() => selectDealer(dealer)}
                    className="w-full px-4 py-3 text-left hover:bg-green-50 border-b transition">
                    <div className="font-medium text-gray-800">{dealer.client_name}</div>
                    <div className="text-xs text-gray-500">
                      P.IVA: {dealer.client_tax_id || "N/A"} - {dealer.appointment_count} appuntamenti
                    </div>
                    {dealer.locations?.length > 1 && (
                      <div className="text-xs text-blue-600 mt-1 flex items-center gap-1">
                        <MapPin size={12} />{dealer.locations.length} sedi disponibili
                      </div>
                    )}
                  </button>
                ))}
              </>
            )}
            {!isSearching && osmSuggestions.length > 0 && (
              <>
                <div className="px-4 py-2 border-b sticky top-0" style={{
                  backgroundColor: osmSuggestions[0]?.source === 'registro_imprese' ? '#fef3c7' : '#dbeafe'
                }}>
                  <span className={`text-xs font-semibold ${
                    osmSuggestions[0]?.source === 'registro_imprese' ? 'text-amber-700' : 'text-blue-700'
                  }`}>
                    {osmSuggestions[0]?.source === 'registro_imprese'
                      ? `REGISTRO IMPRESE (${osmSuggestions.length})`
                      : `DA OPENSTREETMAP (${osmSuggestions.length})`}
                  </span>
                </div>
                {osmSuggestions.map((item, idx) => (
                  <button key={`ext-${idx}`} type="button" onClick={() => selectOsmResult(item)}
                    className={`w-full px-4 py-3 text-left border-b last:border-b-0 transition ${
                      item.source === 'registro_imprese' ? 'hover:bg-amber-50' : 'hover:bg-blue-50'
                    }`}>
                    <div className="font-medium text-gray-800">{item.client_name || item.name}</div>
                    {item.source === 'registro_imprese' && item.piva && (
                      <div className="text-xs text-amber-600 font-medium">P.IVA: {item.piva}</div>
                    )}
                    <div className="text-xs text-gray-500">
                      {item.address && `${item.address}, `}{item.city} {item.province && `(${item.province})`}
                    </div>
                    {item.postcode && <div className="text-xs text-gray-400">CAP: {item.postcode}</div>}
                  </button>
                ))}
              </>
            )}
            {!isSearching && dealerSuggestions.length === 0 && osmSuggestions.length === 0 && (
              <div className="px-4 py-3 text-center text-gray-500 text-sm">
                Nessun risultato trovato. Puoi inserire i dati manualmente.
              </div>
            )}
          </div>
        )}

        {/* Location Selection Dropdown */}
        {showLocationDropdown && selectedDealerLocations.length > 0 && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-blue-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
            <div className="px-4 py-2 bg-blue-50 border-b border-blue-200">
              <span className="text-sm font-medium text-blue-800">Seleziona la sede:</span>
            </div>
            {selectedDealerLocations.map((loc, idx) => (
              <button key={idx} type="button" onClick={() => selectLocation(loc)}
                className="w-full px-4 py-3 text-left hover:bg-blue-50 border-b last:border-b-0 transition">
                <div className="flex items-center gap-2">
                  <MapPin size={14} className="text-blue-600" />
                  <div>
                    <div className="font-medium text-gray-800">{loc.address}</div>
                    <div className="text-sm text-gray-600">{loc.city} ({loc.province})</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Member Number
          <span className="text-xs text-gray-400 ml-2">(ID Cliente/Membro)</span>
        </label>
        <input
          type="text"
          value={formData.member_number}
          onChange={(e) => setFormData({ ...formData, member_number: e.target.value })}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-lojack-red"
          placeholder="Es: MBR-12345"
          data-testid="appointment-member-number"
        />
      </div>
    </div>
  );
};

export default ClientSection;
